import {useState, useEffect, useCallback, useMemo} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {isEqual} from "lodash";
// local
import {
  getStorage,
  setStorage,
  getSessionStorage,
  setSessionStorage,
} from "helpers/storage";
import {
  isEmptyObject,
  filterObjectValidKeys,
  getUrlParams,
  parseUrlParams,
  normalizeParams,
} from "helpers/helper";

const getData = {
  local: (key) => getStorage(key),
  session: (key) => getSessionStorage(key),
};
const setData = {
  local: (key, value) => setStorage(key, value),
  session: (key, value) => setSessionStorage(key, value),
};

export const useSavedFilters = ({
  key,
  defaultValue = {},
  initialValue,
  allowedValues = {},
  allowedKeys,
  listTypeKeys,
  updateFilters,
  storage = "local",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setInnerFilters] = useState(
    initialValue ? filterObjectValidKeys(initialValue) : null
  );
  const [isInitial, setIsInitial] = useState(false);
  const isInitialParams = useMemo(
    () =>
      isEqual(
        {...filters},
        initialValue ? filterObjectValidKeys(initialValue) : defaultValue
      ),
    [filters, initialValue, defaultValue]
  );

  const updateUrlParams = useCallback(
    (values) => {
      navigate({search: getUrlParams(values), replace: true});
    },
    [navigate]
  );

  const setFilters = useCallback(
    (values) => {
      const params = {...values};
      Object.assign(params, {page: isNaN(params.page) ? 1 : +params.page});
      setData[storage](key, JSON.stringify(params));
      updateUrlParams(params);
      setInnerFilters(params);
      updateFilters && updateFilters(params);
    },
    [key, updateFilters, updateUrlParams, storage]
  );

  const clearFilters = () => setFilters({...defaultValue});

  useEffect(() => {
    const urlParams = parseUrlParams(
      Object.fromEntries(new URLSearchParams(location.search))
    );
    const storageParams = JSON.parse(getData[storage](key));
    const params = isEmptyObject(urlParams)
      ? storageParams ?? defaultValue
      : {...defaultValue, ...urlParams};

    Object.assign(params, {page: isNaN(params.page) ? 1 : +params.page});

    setFilters(normalizeParams({params, allowedValues, allowedKeys, listTypeKeys}));
    setIsInitial(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  return [filters, setFilters, clearFilters, isInitial, isInitialParams];
};
